/**
 * Checklist Instance Service
 *
 * Wraps the polymorphic ChecklistInstance endpoints — the bridge
 * between checklist *templates* (managed in the CMS) and the
 * Work Orders or Inspections they're assigned to.
 *
 * On assignment the server snapshots the template's items into
 * ChecklistItem rows under a new ChecklistInstance. Future template
 * edits never alter in-flight work because each instance carries its
 * own copy of the field values.
 *
 * Backend endpoints (under /api/v1):
 *   GET    /work-orders/{id}/checklist
 *   POST   /work-orders/{id}/checklist                 (body: { checklistId })
 *   POST   /work-orders/{id}/checklist/items
 *   GET    /inspections/{id}/checklist
 *   POST   /inspections/{id}/checklist                 (body: { checklistId })
 *   POST   /inspections/{id}/checklist/items
 *   PATCH  /checklist-items/{id}
 *   POST   /checklist-items/{id}/toggle
 *   DELETE /checklist-items/{id}
 */

import { api, camelizeKeys } from './api';
import type { InspectionRating } from '../utils/inspectionRating';

/* ============================================
 * TYPES
 * ============================================ */

/**
 * Inspection rating — one of four states (or null when un-rated).
 * Stays NULL on work_order-typed instance items, which use isCompleted
 * instead. The two interaction models are intentionally orthogonal so
 * neither column has to encode both states.
 */
export type ChecklistItemRating = InspectionRating;

export interface ChecklistInstanceItem {
    id: string;
    checklistInstanceId: string;
    templateItemId: string | null;
    title: string;
    details: string | null;
    additionalDetails: string | null;
    /** 'none' | 'photo' | 'before_after' — see ChecklistItemPhotoMode. */
    photoMode: 'none' | 'photo' | 'before_after';
    /** Whether the photo(s) implied by photoMode block completion. */
    photoRequired: boolean;
    isRequired: boolean;
    sortOrder: number;
    isCompleted: boolean;
    /** Inspection rating — null on WO instances (where isCompleted is used). */
    rating: ChecklistItemRating | null;
    /**
     * Why this tier was chosen. Required when rating is 'critical' — that
     * finding escalates to management, and the note is what lands in the
     * escalation email.
     */
    ratingNote: string | null;
    /** Retired four-state note column, retained for historical rows only. */
    dissatisfiedNote: string | null;
    /* ── Critical escalation ledger ──────────────────────────────────────
     * Populated only on items rated critical. flagged → the clock started;
     * notified → operations was emailed; escalated → it went up to the branch
     * manager after the window; resolved → someone closed it out. */
    criticalFlaggedAt: string | null;
    criticalNotifiedAt: string | null;
    criticalEscalatedAt: string | null;
    criticalResolvedAt: string | null;
    criticalResolvedById: string | null;
    completedById: string | null;
    completedBy?: {
        id: string;
        firstName: string;
        lastName: string;
    } | null;
    completedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface ChecklistInstance {
    id: string;
    assignableType: string;
    assignableId: string;
    sourceChecklistId: string | null;
    sourceVersion: number | null;
    items: ChecklistInstanceItem[];
    sourceChecklist?: {
        id: string;
        title: string;
        version: number;
        isActive: boolean;
    } | null;
    /** Items holding work (ticks, ratings, notes, signatures, photos) that
     *  replacing this checklist would remove — counted by the server, which
     *  refuses a replacement while it is above zero unless confirmed. */
    progressCount?: number;
    createdAt: string;
    updatedAt: string;
}

/**
 * Payload for assigning a checklist template to a parent. The server
 * looks up the template, snapshots its items, and (if an instance
 * already exists) replaces it.
 */
export interface AssignChecklistData {
    checklistId: number;
    /** Confirms replacing a checklist that holds work. Without it the server
     *  answers 409 with progress_count rather than deleting that work. */
    replaceProgress?: boolean;
}

/**
 * Payload shape for adding an ad-hoc instance item — fields mirror
 * ChecklistTemplateItem but without an id (server assigns one).
 */
export interface AddInstanceItemData {
    title: string;
    details?: string | null;
    additionalDetails?: string | null;
    photoMode?: 'none' | 'photo' | 'before_after';
    photoRequired?: boolean;
    isRequired?: boolean;
    sortOrder?: number;
}

/**
 * Patch payload — any subset of the editable fields on an existing
 * instance item. Used by managers tweaking the checklist on a
 * specific WO/Inspection (not propagated back to the template).
 */
export interface UpdateInstanceItemData {
    title?: string;
    details?: string | null;
    additionalDetails?: string | null;
    photoMode?: 'none' | 'photo' | 'before_after';
    photoRequired?: boolean;
    isRequired?: boolean;
    sortOrder?: number;
}

/* ============================================
 * SERVICE
 * ============================================ */

export const checklistInstanceService = {
    /** GET /work-orders/{id}/checklist — null when no checklist assigned yet.
     *  The API returns snake_case keys; we camelize the instance here so every
     *  consumer gets the camelCase shape its ChecklistInstance type promises
     *  (e.g. sourceChecklistId / sourceChecklist). Without this the source
     *  template id reads back undefined and the picker looks unassigned. */
    getForWorkOrder: async (workOrderId: string) => {
        const res = await api.get<{ data: unknown }>(`/work-orders/${workOrderId}/checklist`);
        const instance = camelizeKeys(res.data?.data ?? null) as ChecklistInstance | null;
        return { ...res, data: { data: instance } };
    },

    /** GET /inspections/{id}/checklist — camelized for the same reason as above. */
    getForInspection: async (inspectionId: string) => {
        const res = await api.get<{ data: unknown }>(`/inspections/${inspectionId}/checklist`);
        const instance = camelizeKeys(res.data?.data ?? null) as ChecklistInstance | null;
        return { ...res, data: { data: instance } };
    },

    /**
     * POST /work-orders/{id}/checklist — assign or replace.
     * If an instance exists, the server deletes its items and snapshots
     * fresh from the template.
     */
    assignToWorkOrder: (workOrderId: string, data: AssignChecklistData) =>
        api.post<{ data: ChecklistInstance }>(`/work-orders/${workOrderId}/checklist`, data),

    /** POST /inspections/{id}/checklist */
    assignToInspection: (inspectionId: string, data: AssignChecklistData) =>
        api.post<{ data: ChecklistInstance }>(`/inspections/${inspectionId}/checklist`, data),

    /** POST /work-orders/{id}/checklist/items — ad-hoc item */
    addItemToWorkOrder: (workOrderId: string, data: AddInstanceItemData) =>
        api.post<{ data: ChecklistInstanceItem }>(`/work-orders/${workOrderId}/checklist/items`, data),

    /** POST /inspections/{id}/checklist/items — ad-hoc item */
    addItemToInspection: (inspectionId: string, data: AddInstanceItemData) =>
        api.post<{ data: ChecklistInstanceItem }>(`/inspections/${inspectionId}/checklist/items`, data),

    /** PATCH /checklist-items/{id} — per-instance edit */
    updateItem: (itemId: string, data: UpdateInstanceItemData) =>
        api.patch<{ data: ChecklistInstanceItem }>(`/checklist-items/${itemId}`, data),

    /** POST /checklist-items/{id}/toggle — flip is_completed and capture who/when */
    toggleItem: (itemId: string, isCompleted: boolean) =>
        api.post<{ data: ChecklistInstanceItem }>(`/checklist-items/${itemId}/toggle`, { isCompleted }),

    /**
     * POST /checklist-items/{id}/rating — set the tier (or clear it with
     * rating=null).
     *
     * ratingNote MUST be a non-empty string when rating='critical'; the server
     * returns 422 otherwise. A critical finding emails operations immediately
     * and escalates to the branch manager if it stays open, so "critical" with
     * no explanation is not actionable by anyone receiving it.
     */
    rateItem: (itemId: string, rating: ChecklistItemRating | null, ratingNote?: string | null) =>
        api.post<{ data: ChecklistInstanceItem }>(`/checklist-items/${itemId}/rating`, {
            rating,
            ratingNote: ratingNote ?? null,
        }),

    /**
     * POST /checklist-items/{id}/resolve-critical — close out a critical
     * finding. Stops the branch-manager escalation clock and records who
     * signed it off.
     */
    resolveCritical: (itemId: string) =>
        api.post<{ data: ChecklistInstanceItem }>(`/checklist-items/${itemId}/resolve-critical`),

    /** DELETE /checklist-items/{id} */
    removeItem: (itemId: string) =>
        api.delete<void>(`/checklist-items/${itemId}`),
};
